import { actions, assign, createMachine, sendParent } from 'xstate';
import { NavigationSections } from '../constants';
import { AppSteps, BankConnectionErrTypes, ChannelTypes, FormTypes, SoftDeclineTypes, UpdateBankAccountErrors, UpdateDebitErrors } from '../gql/graphql';
import { isPostDecisionStatus } from '../helpers/converters/isPostDecisionStatus';
import { getEcommApprovalOnly } from '../helpers/leases/leases.helpers';
import { plaidUpdateBankAccount, updateBankAccount } from '../networkRequests/bankAccount/updateBankAccount';
import updateDebitInfo from '../networkRequests/debitCard/updateDebitCard';
import updateEmployment from '../networkRequests/employment/updateEmployment';
import queryOrder from '../networkRequests/order/queryOrder';
import fetchLinkToken from '../networkRequests/plaid/getLinkToken';
import { PaymentSelectionErrors } from '../pages/paymentSelectionError/PaymentSelectionError';
import getApplicationId from '../utils/commonState/getApplicationId';
import { PropagateNetworkErrors } from './actions/genericActions';
import { identify, page, track } from './actions/segment';
import { MakeStateCallingQueryOrder, MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import {
  ExistingApplicationEvents,
  ExistingApplicationMachineContext,
  ExistingApplicationMachineEvents,
  ExistingApplicationMachineGuards,
  ExistingApplicationMachineStates,
  ExistingApplicationStates,
} from './types/existingApplicationMachine.types';
import { determinePlaidFF } from '../helpers/plaid/plaid.helper';
import { EcommMachineEvents } from './types/ecommMachine.types';
import { PostDecisionStates } from './types/postDecisionMachine.types';
import { FLAG_SHOW_DEBIT_DECISION_SHORT_FORM } from '../constants/feature-flags';

// OR-3068 / OR-3067: the post-decision pages (approval, cart, contract summary, signing, the
// virtual card page) live in post-decision-machine. Every transition in this machine that used to
// target one of them exits through ExistingApplicationStates.PostDecision instead, so this machine
// never renders them. OR-2999 deleted the copies that used to live here.
//
// The exit carries postDecisionStartingPage, which post-decision-machine honours as skipToPage.
// It is only set where the lease status alone would route somewhere else (its Entry lists the
// values it honours); left undefined, the submachine derives the page from context.
const exitToPostDecision = (postDecisionStartingPage?: string) => ({
  target: `#ExistingApplication.${ExistingApplicationStates.PostDecision}`,
  actions: assign({ postDecisionStartingPage }),
});

// The debit card update link on the post-decision contract summary sets fromContractSummary; the
// return has to go back to that page rather than wherever the status derives to.
const returnToContractSummary = {
  cond: (ctx: ExistingApplicationMachineContext) => ctx.fromContractSummary,
  target: `#ExistingApplication.${ExistingApplicationStates.PostDecision}`,
  actions: assign({
    postDecisionStartingPage: (ctx: ExistingApplicationMachineContext) =>
      ctx.dealer?.channelType === ChannelTypes.Ecommerce ? PostDecisionStates.ContractSummaryEcomm : PostDecisionStates.ContractSummaryBM,
  }),
};

export default createMachine<ExistingApplicationMachineContext, ExistingApplicationMachineEvents, ExistingApplicationMachineStates>(
  {
    id: 'ExistingApplication',
    initial: ExistingApplicationStates.ApplicationExists,
    context: {} as ExistingApplicationMachineContext, // Nothing you put here matters, the init block is the data in the `src: ExistingApplicationMachine` line in ecomm-machine
    on: {
      [ExistingApplicationEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [ExistingApplicationEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [ExistingApplicationEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [ExistingApplicationStates.ApplicationExists]: {
        always: [
          { target: ExistingApplicationStates.VirtualCardApplication, cond: 'VirtualCardDealer' },
          { target: ExistingApplicationStates.LeaseApplication },
        ],
      },
      [ExistingApplicationStates.LeaseApplication]: {
        initial: 'Select',
        states: {
          Select: {
            always: [
              // The contract summary debit link and the DebitCardError NEXT handoff arrive with this
              // skip. The link alone gets the header back, through fromContractSummary. The link
              // arrives approved, so the skip sits ahead of the exit below.
              {
                target: ExistingApplicationStates.DebitCardEntry,
                cond: 'LeaseSkipToDebitCardEntry',
                actions: assign({ canGoBackViaHeader: (ctx) => !!ctx.fromContractSummary }),
              },
              // Decided statuses belong to post-decision-machine. The ecomm cascade and the
              // customer lookup route them there first. The new application approved handoff does
              // not, so this exit is live. Ahead of the appStep resumes because AES leaves appStep on
              // the last step after the decision.
              { cond: (ctx) => isPostDecisionStatus(ctx.application), ...exitToPostDecision() },
              { target: ExistingApplicationStates.PaymentSelection, cond: 'EnterPaymentSelectFF' },
              // Carries the entrypoint BankAccountError handoff. Select has no BankDetails skip.
              { target: ExistingApplicationStates.BankDetails, cond: 'AppStepBankingInformation' },
              { target: ExistingApplicationStates.DebitCardEntry, cond: 'AppStepDebitInformation' },
              { target: ExistingApplicationStates.BankAccountError, cond: 'LeaseBankAccountError' },
              { target: ExistingApplicationStates.DebitCardError, cond: 'LeasePaymentError' },
              { target: ExistingApplicationStates.Prequalification, cond: 'LeasePrequalified' },
              { target: ExistingApplicationStates.PayFrequency },
            ],
          },
          [ExistingApplicationStates.Prequalification]: {
            // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
            initial: 'callingQueryOrder',
            states: {
              callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
              waiting: { on: { [ExistingApplicationEvents.NEXT]: { target: '#ExistingApplication.LeaseApplication.PayFrequency' } } },
            },
            meta: {
              navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
            },
          },
          [ExistingApplicationStates.PayFrequency]: {
            initial: 'PayFrequencyInit',
            states: {
              [ExistingApplicationStates.PayFrequencyInit]: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: ExistingApplicationStates.UpdateEmployment,
                      actions: [actions.assign({ payFrequencyDetails: (_context, event) => event.data })],
                    },
                  ],
                },
              },
              [ExistingApplicationStates.UpdateEmployment]: {
                tags: 'calling',
                invoke: {
                  src: (context) => updateEmployment(context.application, context.payFrequencyDetails),
                  onDone: [
                    {
                      cond: 'HasUpdateEmploymentErrors',
                      target: 'PayFrequencyInit',
                      actions: [assign({ updateEmploymentErrors: (_, event) => event.data?.updateEmploymentErrors })],
                    },
                    { target: ExistingApplicationStates.RefreshAfterEmployment },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RefreshAfterEmployment]: {
                tags: 'calling',
                invoke: {
                  src: queryOrder,
                  onDone: {
                    target: ExistingApplicationStates.RouteAfterEmployment,
                    actions: assign({
                      application: (_, event) => event.data?.application,
                      orderDetails: (_, event) => event.data?.details,
                    }),
                  },
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RouteAfterEmployment]: {
                always: [
                  // Feature flag takes precedence over all other checks.
                  // If enabled, show new payment selection screen
                  { target: '#ExistingApplication.LeaseApplication.PaymentSelection', cond: 'PaymentSelectFF' },
                  {
                    target: '#ExistingApplication.LeaseApplication.BankDetails',
                    cond: 'PerformBav',
                    actions: [actions.assign({ canGoBackViaHeader: true })],
                  },
                  // Debit Card entry is the default flow
                  { target: '#ExistingApplication.LeaseApplication.DebitCardEntry' },
                ],
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.BankAccountError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.BankDetails]: {
            initial: 'waiting',
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingUpdateBankAccount',
                      actions: [assign({ bankDetails: (_, event) => event?.data })],
                    },
                  ],
                },
              },
              callingUpdateBankAccount: {
                tags: 'calling',
                invoke: {
                  src: (context, event) =>
                    // We know application will have id at this point in the flow
                    // We also know that bank account is only currently present for leases
                    updateBankAccount(context.application?.lease!.id!, context.bankDetails?.accountNumber, context.bankDetails?.routingNumber),
                  onDone: [
                    {
                      cond: (_, event) => !!event.data.softDeclineError,
                      target: 'waiting',
                      actions: [
                        assign({
                          canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short,
                          updateBankAccountErr: (_, event) => event.data.softDeclineError,
                        }),
                      ],
                    },
                    {
                      cond: (_, event) => !!event.data.hardDeclineError,
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      target: '#ExistingApplication.LeaseApplication.DebitCardEntry',
                      actions: [assign({ canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short })],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                {
                  target: '#ExistingApplication.LeaseApplication.PaymentSelection',
                  cond: 'PaymentSelectFF',
                },
                {
                  target: '#ExistingApplication.LeaseApplication.PayFrequency',
                },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.PaymentSelection]: {
            initial: 'PaymentSelectionInit',
            states: {
              PaymentSelectionInit: {
                on: {
                  [ExistingApplicationEvents.SELECT_PLAID]: { target: 'callingFetchLinkToken' },
                  [ExistingApplicationEvents.SELECT_MANUAL]: { target: '#ExistingApplication.LeaseApplication.BankDetails' },
                  [ExistingApplicationEvents.BACK]: { target: '#ExistingApplication.LeaseApplication.PayFrequency' },
                },
              },
              callingFetchLinkToken: {
                invoke: {
                  src: (context) => fetchLinkToken(context.orderId),
                  onDone: [
                    {
                      cond: 'LinkTokenFailure',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    },
                    {
                      target: '#ExistingApplication.LeaseApplication.PlaidModal',
                      actions: assign({
                        plaidToken: (_context, event: any) => {
                          return {
                            linkToken: event.data.linkToken,
                            publicToken: '',
                          };
                        },
                      }),
                    },
                  ],
                  onError: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.PlaidModal]: {
            initial: 'PlaidModalInit',
            states: {
              PlaidModalInit: {
                on: {
                  [ExistingApplicationEvents.BACK]: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelection',
                  },
                  [ExistingApplicationEvents.ERROR]: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                    actions: [
                      actions.assign({
                        bankConnection: (context, event) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    ],
                  },
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'PlaidUpdateBankAccount',
                      actions: [
                        actions.assign({
                          bankDetails: (context, event) => {
                            return { ...context.bankDetails, bankName: event?.data?.bankName };
                          },
                          bankConnection: (context, event) => {
                            return { ...context.bankConnection, bankAcctLast4: event?.data?.bankAcctLast4 };
                          },
                          plaidToken: (context, event) => {
                            return { ...context.plaidToken, publicToken: event?.data?.publicToken };
                          },
                        }),
                      ],
                    },
                  ],
                },
              },
              PlaidUpdateBankAccount: {
                invoke: {
                  id: 'updateBankAccount',
                  src: (context, event) =>
                    // We know application will have id at this point in the flow
                    // We also know that bank account is only currently present for leases
                    plaidUpdateBankAccount(context.application?.lease!.id!, context.plaidToken.publicToken, context.plaidToken.linkToken),
                  onDone: [
                    {
                      cond: 'TokenizedAcct',
                      target: '#ExistingApplication.LeaseApplication.BankDetails',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, isTokenizedAcct: true };
                        },
                      }),
                    },
                    {
                      cond: 'InternetBankError',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.internetBankError };
                        },
                      }),
                    },
                    {
                      cond: 'BankError',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError };
                        },
                      }),
                    },
                    {
                      cond: 'BankAccountHardDecline',
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      cond: 'IneligibleBank',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.ineligibleError };
                        },
                      }),
                    },
                    {
                      cond: 'PlaidSuccess',
                      target: '#ExistingApplication.LeaseApplication.PaymentReview',
                    },
                  ],

                  onError: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
          },

          [ExistingApplicationStates.PaymentSelectionError]: {
            on: {
              [ExistingApplicationEvents.SELECT_PLAID]: [{ target: ExistingApplicationStates.PaymentSelection }],
              [ExistingApplicationEvents.SELECT_MANUAL]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
          },
          [ExistingApplicationStates.PaymentReview]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
          },
          [ExistingApplicationStates.DebitCardError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.DebitCardEntry]: {
            initial: 'waiting',
            // cleared on the way out rather than on entering select, so the returnToContractSummary
            // guards still see it; a prepaid card retry stays inside and keeps it. The header back
            // goes with it so the contract summary does not inherit it through the handoff.
            exit: assign({ fromContractSummary: false, canGoBackViaHeader: false }),
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: {
                    target: 'callingUpdateDebitInfo',
                    actions: assign({
                      debitInfo: (_context, event) => event.data.debitDetails,
                      billingAddress: (_context, event) => event.data.billingAddress,
                    }),
                  },
                },
              },
              callingUpdateDebitInfo: {
                tags: 'calling',
                invoke: {
                  src: (context: ExistingApplicationMachineContext, event: ExistingApplicationMachineEvents) =>
                    updateDebitInfo(getApplicationId(context.application), context.debitInfo, context.billingAddress!),
                  onDone: [
                    {
                      target: 'callingQueryOrder',
                      actions: assign({ updateDebitInfoErrors: (_context, event) => event.data.updateDebitInfoErrors }),
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingQueryOrder: MakeStateCallingQueryOrder('select'),
              select: {
                always: [
                  {
                    target: '#ExistingApplication.DebitCardHardDecline',
                    cond: 'DebitCardHardDecline',
                  },
                  {
                    target: 'waiting',
                    cond: 'DebitCardPrepaidError',
                  },
                  {
                    target: '#ExistingApplication.LeaseApplication.DebitCardError',
                    cond: 'DebitCardSoftDeclineAndErrorDetailsEnabled',
                  },
                  {
                    target: '#ExistingApplication.DebitCardSoftDecline',
                    cond: 'DebitCardSoftDecline',
                  },
                  {
                    target: '#ExistingApplication.DebitCardFatalError',
                    cond: 'DebitCardFatalError',
                  },
                  //check errors first before evaluating success transition
                  // OR-3068: the initial payment is authorized and the rest is post-decision-machine's,
                  // which derives the page from the refreshed order. The one pin is the return from
                  // the debit card update link.
                  returnToContractSummary,
                  exitToPostDecision(),
                ],
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                // the contract summary link came from post-decision-machine, so BACK returns there
                returnToContractSummary,
                { target: ExistingApplicationStates.BankDetails, cond: 'PerformBav' },
                { target: ExistingApplicationStates.PayFrequency },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
        },
      },
      [ExistingApplicationStates.VirtualCardApplication]: {
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation(ExistingApplicationStates.Loading),
          [ExistingApplicationStates.Loading]: {
            always: [
              // Decided statuses exit to post-decision-machine (OR-3067). The pre-decision skips stay
              // ahead of that exit.
              { target: ExistingApplicationStates.PaymentSelection, cond: 'EnterPaymentSelectFF' },
              // The entrypoint BankAccountError handoff. Only this router honours the skip.
              { target: ExistingApplicationStates.BankDetails, cond: 'LeaseSkipToBankDetails' },
              // The contract summary debit link and the DebitCardError NEXT handoff. The link alone
              // gets the header back, through fromContractSummary.
              {
                target: ExistingApplicationStates.DebitCardEntry,
                cond: 'LeaseSkipToDebitCardEntry',
                actions: assign({ canGoBackViaHeader: (ctx) => !!ctx.fromContractSummary }),
              },
              { cond: (ctx) => isPostDecisionStatus(ctx.application), ...exitToPostDecision() },
              { target: ExistingApplicationStates.CheckLeaseStatus },
            ],
            meta: {
              navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
            },
          },
          [ExistingApplicationStates.Prequalification]: {
            // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
            initial: 'callingQueryOrder',
            states: {
              callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
              waiting: { on: { [ExistingApplicationEvents.NEXT]: { target: '#ExistingApplication.VirtualCardApplication.PayFrequency' } } },
            },
            meta: {
              navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
            },
          },
          [ExistingApplicationStates.CheckLeaseStatus]: {
            always: [
              { target: ExistingApplicationStates.BankAccountError, cond: 'LeaseBankAccountError' },
              { target: ExistingApplicationStates.DebitCardError, cond: 'LeasePaymentError' },
              { target: ExistingApplicationStates.Prequalification, cond: 'LeasePrequalified' },
              { target: ExistingApplicationStates.PayFrequency },
            ],
          },

          [ExistingApplicationStates.PayFrequency]: {
            initial: 'PayFrequencyInit',
            states: {
              [ExistingApplicationStates.PayFrequencyInit]: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: ExistingApplicationStates.UpdateEmployment,
                      actions: [actions.assign({ payFrequencyDetails: (_context, event) => event.data })],
                    },
                  ],
                },
              },
              [ExistingApplicationStates.UpdateEmployment]: {
                tags: 'calling',
                invoke: {
                  src: (context) => updateEmployment(context.application, context.payFrequencyDetails),
                  onDone: [
                    {
                      cond: 'HasUpdateEmploymentErrors',
                      target: 'PayFrequencyInit',
                      actions: [assign({ updateEmploymentErrors: (_, event) => event.data?.updateEmploymentErrors })],
                    },
                    { target: ExistingApplicationStates.RefreshAfterEmployment },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RefreshAfterEmployment]: {
                tags: 'calling',
                invoke: {
                  src: queryOrder,
                  onDone: {
                    target: ExistingApplicationStates.RouteAfterEmployment,
                    actions: assign({
                      application: (_, event) => event.data?.application,
                      orderDetails: (_, event) => event.data?.details,
                    }),
                  },
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RouteAfterEmployment]: {
                always: [
                  { target: '#ExistingApplication.VirtualCardApplication.PaymentSelection', cond: 'PaymentSelectFF' },
                  {
                    cond: 'PerformBav',
                    target: '#ExistingApplication.VirtualCardApplication.BankDetails',
                    actions: [actions.assign({ canGoBackViaHeader: true })],
                  },
                  // Debit Card entry is the default flow
                  { target: '#ExistingApplication.VirtualCardApplication.DebitCardEntry' },
                ],
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },

          [ExistingApplicationStates.BankAccountError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },

          [ExistingApplicationStates.BankDetails]: {
            initial: 'waiting',
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingUpdateBankAccount',
                      actions: [assign({ bankDetails: (_, event) => event?.data })],
                    },
                  ],
                },
              },
              callingUpdateBankAccount: {
                tags: 'calling',
                invoke: {
                  src: (context, event) =>
                    // We know application will have id at this point in the flow
                    // We also know that bank account is only currently present for leases
                    updateBankAccount(context.application?.lease!.id!, context.bankDetails?.accountNumber, context.bankDetails?.routingNumber),
                  onDone: [
                    {
                      cond: (_, event) => !!event.data.softDeclineError,
                      target: 'waiting',
                      actions: [
                        assign({
                          canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short,
                          updateBankAccountErr: (_, event) => event.data.softDeclineError,
                        }),
                      ],
                    },
                    {
                      cond: (_, event) => !!event.data.hardDeclineError,
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.DebitCardEntry',
                      actions: [assign({ canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short })],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [{ target: '#ExistingApplication.VirtualCardApplication.PayFrequency' }],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },

          [ExistingApplicationStates.PaymentSelection]: {
            initial: 'PaymentSelectionInit',
            states: {
              PaymentSelectionInit: {
                on: {
                  [ExistingApplicationEvents.SELECT_PLAID]: { target: 'callingFetchLinkToken' },
                  [ExistingApplicationEvents.SELECT_MANUAL]: { target: '#ExistingApplication.VirtualCardApplication.BankDetails' },
                  [ExistingApplicationEvents.BACK]: { target: '#ExistingApplication.VirtualCardApplication.PayFrequency' },
                },
              },
              callingFetchLinkToken: {
                invoke: {
                  src: (context) => fetchLinkToken(context.orderId),
                  onDone: [
                    {
                      cond: 'LinkTokenFailure',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.PlaidModal',
                      actions: assign({
                        plaidToken: (_context, event: any) => {
                          return {
                            linkToken: event.data.linkToken,
                            publicToken: '',
                          };
                        },
                      }),
                    },
                  ],
                  onError: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.PlaidModal]: {
            initial: 'PlaidModalInit',
            states: {
              PlaidModalInit: {
                on: {
                  [ExistingApplicationEvents.BACK]: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelection',
                  },
                  [ExistingApplicationEvents.ERROR]: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                    actions: [
                      actions.assign({
                        bankConnection: (context, event) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    ],
                  },
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'PlaidUpdateBankAccount',
                      actions: [
                        actions.assign({
                          bankDetails: (context, event) => {
                            return { ...context.bankDetails, bankName: event?.data?.bankName };
                          },
                          bankConnection: (context, event) => {
                            return { ...context.bankConnection, bankAcctLast4: event?.data?.bankAcctLast4 };
                          },
                          plaidToken: (context, event) => {
                            return { ...context.plaidToken, publicToken: event?.data?.publicToken };
                          },
                        }),
                      ],
                    },
                  ],
                },
              },
              PlaidUpdateBankAccount: {
                invoke: {
                  id: 'updateBankAccount',
                  src: (context, event) =>
                    plaidUpdateBankAccount(context.application?.lease!.id!, context.plaidToken.publicToken, context.plaidToken.linkToken),
                  onDone: [
                    {
                      cond: 'TokenizedAcct',
                      target: '#ExistingApplication.VirtualCardApplication.BankDetails',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, isTokenizedAcct: true };
                        },
                      }),
                    },
                    {
                      cond: 'InternetBankError',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.internetBankError };
                        },
                      }),
                    },
                    {
                      cond: 'BankError',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError };
                        },
                      }),
                    },
                    {
                      cond: 'BankAccountHardDecline',
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      cond: 'IneligibleBank',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.ineligibleError };
                        },
                      }),
                    },
                    {
                      cond: 'PlaidSuccess',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentReview',
                    },
                  ],

                  onError: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
          },

          [ExistingApplicationStates.PaymentSelectionError]: {
            on: {
              [ExistingApplicationEvents.SELECT_PLAID]: [{ target: ExistingApplicationStates.PaymentSelection }],
              [ExistingApplicationEvents.SELECT_MANUAL]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
          },
          [ExistingApplicationStates.PaymentReview]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
          },

          [ExistingApplicationStates.DebitCardError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.DebitCardEntry]: {
            initial: 'waiting',
            // cleared on the way out rather than on entering select, so the returnToContractSummary
            // guards still see it; a prepaid card retry stays inside and keeps it. The header back
            // goes with it so the contract summary does not inherit it through the handoff.
            exit: assign({ fromContractSummary: false, canGoBackViaHeader: false }),
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: {
                    target: 'callingUpdateDebitInfo',
                    actions: assign({
                      debitInfo: (_context, event) => event.data.debitDetails,
                      billingAddress: (_context, event) => event.data.billingAddress,
                    }),
                  },
                },
              },
              callingUpdateDebitInfo: {
                tags: 'calling',
                invoke: {
                  src: (context: ExistingApplicationMachineContext, event: ExistingApplicationMachineEvents) =>
                    updateDebitInfo(getApplicationId(context.application), context.debitInfo, context.billingAddress!),
                  onDone: [
                    {
                      target: 'callingQueryOrder',
                      actions: assign({ updateDebitInfoErrors: (_context, event) => event.data.updateDebitInfoErrors }),
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingQueryOrder: MakeStateCallingQueryOrder('select'),
              select: {
                always: [
                  {
                    target: '#ExistingApplication.DebitCardHardDecline',
                    cond: 'DebitCardHardDecline',
                  },
                  {
                    target: 'waiting',
                    cond: 'DebitCardPrepaidError',
                  },
                  {
                    target: '#ExistingApplication.VirtualCardApplication.DebitCardError',
                    cond: 'DebitCardSoftDeclineAndErrorDetailsEnabled',
                  },
                  {
                    target: '#ExistingApplication.DebitCardSoftDecline',
                    cond: 'DebitCardSoftDecline',
                  },
                  {
                    target: '#ExistingApplication.DebitCardFatalError',
                    cond: 'DebitCardFatalError',
                  },
                  //check errors first before evaluating success transition
                  // debit card update guard
                  returnToContractSummary,
                  // Normally this would go to approval due to status checks but
                  // approval is pinned here because shop first flows may already have a valid cart already
                  // which would inadvertently skip the approval page.
                  exitToPostDecision(PostDecisionStates.Approval),
                ],
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                // the contract summary link came from post-decision-machine, so BACK returns there
                returnToContractSummary,
                { target: ExistingApplicationStates.BankDetails, cond: 'PerformBav' },
                { target: ExistingApplicationStates.PayFrequency },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
        },
      },
      [ExistingApplicationStates.DebitCardHardDecline]: {},
      [ExistingApplicationStates.DebitCardSoftDecline]: {
        on: {
          [ExistingApplicationEvents.NEXT]: [
            {
              target: '#ExistingApplication.VirtualCardApplication.DebitCardEntry',
              cond: 'VirtualCardDealer',
            },
            { target: '#ExistingApplication.LeaseApplication.DebitCardEntry' },
          ],
        },
      }, // needs to go back to IP
      [ExistingApplicationStates.DebitCardFatalError]: {},
      [ExistingApplicationStates.BankAccountHardDecline]: {},
      [ExistingApplicationStates.InternalError]: {},
      // The exit to post-decision-machine (see exitToPostDecision). Final, like the long form
      // machine's: the ecomm machine leaves this state on the event, so nothing runs after it.
      [ExistingApplicationStates.PostDecision]: {
        type: 'final',
        entry: sendParent((context) => ({ type: EcommMachineEvents.POST_DECISION, data: { ...context } })),
      },
    },
    schema: {
      context: {} as ExistingApplicationMachineContext,
      events: {} as ExistingApplicationMachineEvents,
      guards: {} as ExistingApplicationMachineGuards,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      log: (context, event, meta) => {
        console.log('Existing App Machine');
        console.log('current state:', meta.state?.value);
        console.log('context', context);
        console.log('event', event);
      },
    },
    guards: {
      VirtualCardDealer: (context: ExistingApplicationMachineContext) => !!context.dealer?.isVirtualCard,
      // Changing this to always performBAV if the flag
      PerformBav: (context, event) =>
        context.application?.lease?.draftStep === 'bankingInformation' || // normal value
        context.application?.lease?.draftStep === 'bankAccount', // value if in errors found
      DebitCardPage: (context, event) =>
        context.application?.lease?.draftStep === 'debitCardInformation' || // normal value
        context.application?.lease?.draftStep === 'debitCardError', // value if in errors found
      PaymentError: (context: ExistingApplicationMachineContext) => {
        const declineReason = context.application?.lease?.declineReason;
        return ['debitCardError'].includes(declineReason!);
      },
      LeaseSkipToDebitCardEntry: (context: ExistingApplicationMachineContext) => context.skipToPage === 'DebitCardEntry',
      LeaseSkipToBankDetails: (context: ExistingApplicationMachineContext) => context.skipToPage === 'BankDetails', // the entrypoint BankAccountError.NEXT handoff
      LeasePrequalified: (context: ExistingApplicationMachineContext) => context.application?.lease?.status === 'preApproved',
      LeaseDocsSigned: (context: ExistingApplicationMachineContext) => context.application?.lease?.status === 'docsSigned',
      LeaseReadyForFunding: (context: ExistingApplicationMachineContext) => context.application?.lease?.status === 'readyForFunding',
      LeasePaymentError: (context: ExistingApplicationMachineContext) => {
        const declineReason = context.application?.lease?.declineReason;
        return ['creditCardNumber', 'creditCardExpiration', 'debitCardError'].includes(declineReason!);
      },
      LeaseBankAccountError: (context: ExistingApplicationMachineContext) => {
        const declineReason = context.application?.lease?.declineReason;
        return declineReason === 'bankAccount';
      },
      IsBrickAndMortar: (context, event) => {
        return context.dealer?.channelType === ChannelTypes.BrickAndMortar;
      },
      IsEcommApprovalOnly: (context: ExistingApplicationMachineContext) => getEcommApprovalOnly(context.dealer),
      DebitCardHardDecline: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename === 'HardDecline'),
      DebitCardSoftDecline: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename === 'SoftDecline'),
      DebitCardSoftDeclineAndErrorDetailsEnabled: (context: ExistingApplicationMachineContext, _) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename === 'SoftDecline') &&
        context.ldClient.variation(FLAG_SHOW_DEBIT_DECISION_SHORT_FORM, false),
      DebitCardFatalError: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename !== 'PrepaidCardError'),
      DebitCardPrepaidError: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some(
          (err: UpdateDebitErrors) =>
            err.__typename === 'SoftDecline' &&
            (err.extensions?.declineType === SoftDeclineTypes.PrepaidCardsNotAllowed || err.extensions?.declineType === SoftDeclineTypes.CreditCardsNotAllowed),
        ),
      PaymentSelectFF: (context, event) => {
        return determinePlaidFF(context);
      },
      // Long form never resumes on the bank step here, so only the short form appStep is checked.
      EnterPaymentSelectFF: (context, event) =>
        determinePlaidFF(context) && context?.dealer.formType === FormTypes.Short && context?.application?.lease?.appStep === AppSteps.BankingInformation,
      TokenizedAcct: (_context, event) =>
        event.data.updateBankAccountErrors.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'BankConnectionError' && err?.extensions?.errCode === BankConnectionErrTypes.Tokenized,
        ),
      InternetBankError: (_, event) =>
        event.data?.updateBankAccountErrors?.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'SoftDecline' && err?.extensions?.declineType === SoftDeclineTypes.InternetBankError,
        ),
      BankError: (_, event) =>
        event.data?.updateBankAccountErrors?.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'SoftDecline' && err?.extensions?.declineType === SoftDeclineTypes.BankError,
        ),
      BankAccountHardDecline: (context, event) => event.data?.updateBankAccountErrors?.some((err: UpdateBankAccountErrors) => err.__typename === 'HardDecline'),
      IneligibleBank: (_context, event) => {
        // General error coming back, etc...
        return event.data?.updateBankAccountErrors?.length;
      },
      PlaidSuccess: (_context, event) => {
        return event.data.updateBankAccountErrors && event.data.updateBankAccountErrors.length === 0;
      },
      LinkTokenFailure: (_context, event) => {
        return event.data.generateBankConnectorTokenErrors.length > 0;
      },
      HasUpdateEmploymentErrors: (_context, event) => {
        return event.data?.updateEmploymentErrors?.length;
      },
      AppStepPayFrequency: (context) => {
        return context?.application?.lease?.appStep === AppSteps.IncomeSource;
      },
      AppStepBankingInformation: (context) => {
        return context?.application?.lease?.appStep === AppSteps.BankingInformation;
      },
      AppStepDebitInformation: (context) => {
        return context?.application?.lease?.appStep === AppSteps.DebitCardInformation;
      },
    },
  },
);
